/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import {
  computePerItemDiscounts,
  computeSelectedTotal,
  type DiscountableItem,
} from "./types";
import { DiscountItemRow } from "./DiscountItemRow";
import type { CheckoutDiscountComponent } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

interface DiscountItemListProps {
  items: DiscountableItem[];
  selectedQuantities: Record<string, number>;
  existingComponents: CheckoutDiscountComponent[];
  onSelect: (key: string, qty: number) => void;
}

export const DiscountItemList = defineComponent<DiscountItemListProps>({
  name: "DiscountItemList",
  props: ["items", "selectedQuantities", "existingComponents", "onSelect"],
  setup(props) {
    const auth = useAuth();
    const perItemDiscounts = computed(() =>
      computePerItemDiscounts(props.items, props.existingComponents),
    );

    const selectedCount = computed(
      () => Object.values(props.selectedQuantities).filter((q) => q > 0).length,
    );

    const selectedTotal = computed(() =>
      computeSelectedTotal(props.items, props.selectedQuantities),
    );

    return () => (
      <div class="p-1 lg:p-4 flex flex-col overflow-y-hidden select-none h-full">
        {/* Summary bar – mirrors the info stripe in payment flows */}
        <div class="flex items-center justify-between pb-2 mb-1 text-sm border-b border-base-content/10 flex-shrink-0">
          <span class="text-base-content/55">
            {selectedCount.value === 0
              ? "Select items to apply discount"
              : `${selectedCount.value} item${selectedCount.value > 1 ? "s" : ""} selected`}
          </span>
          {selectedTotal.value > 0 && (
            <span class="font-semibold text-accent">
              {formatPrice(
                selectedTotal.value,
                true,
                2,
                auth.user.value?.restaurant.currency || "CHF",
              )}
            </span>
          )}
        </div>

        {/* Item list – styled identically to PartialPaymentContent */}
        <div class="space-y-2 flex flex-col flex-1 overflow-y-auto text-sm lg:text-1xl text-left">
          {props.items.length === 0 ? (
            <div class="flex flex-col items-center justify-center py-12 text-base-content/40">
              <span class="material-symbols-outlined mb-2 text-5xl">
                receipt_long
              </span>
              <p>No items available</p>
            </div>
          ) : (
            props.items.map((item) => (
              <DiscountItemRow
                key={item.key}
                item={item}
                selectedQuantity={props.selectedQuantities[item.key] ?? 0}
                existingDiscountAmount={perItemDiscounts.value[item.key] ?? 0}
                onSelect={props.onSelect}
              />
            ))
          )}
        </div>
      </div>
    );
  },
});
